/**
 * @file date-picker/date-picker.tokens.ts
 *
 * MD3 Design tokens for Date Picker component.
 * Maps from DatePickerModalTokens.kt → CSS variables in packages/tailwind/src/index.css.
 *
 * @see docs/m3/date-and-time-pickers/date-pickers/DatePickerModalTokens.kt
 * @see packages/tailwind/src/index.css
 */

// ─── Color Tokens ─────────────────────────────────────────────────────────────

/** Color tokens — uses CSS custom properties from MD3 theme */
export const DP_COLORS = {
	/** Dialog container — SurfaceContainerHigh */
	container: "var(--md-sys-color-surface-container-high)",
	/** Selected day background — Primary */
	daySelected: "var(--md-sys-color-primary)",
	/** Selected day text — OnPrimary */
	daySelectedText: "var(--md-sys-color-on-primary)",
	/** Today outline — Primary */
	todayOutline: "var(--md-sys-color-primary)",
	/** Today text — Primary */
	todayText: "var(--md-sys-color-primary)",
	/** Unselected day text — OnSurface */
	dayText: "var(--md-sys-color-on-surface)",
	/** Weekday header text — OnSurface */
	weekdayText: "var(--md-sys-color-on-surface)",
	/** Header headline + supporting text — OnSurfaceVariant */
	headerText: "var(--md-sys-color-on-surface-variant)",
	/** Range highlight background — SecondaryContainer */
	rangeHighlight: "var(--md-sys-color-secondary-container)",
	/** Range highlight text — OnSecondaryContainer */
	rangeText: "var(--md-sys-color-on-secondary-container)",
	/** Unselected year text — OnSurfaceVariant */
	yearText: "var(--md-sys-color-on-surface-variant)",
	/** Selected year background — Primary */
	yearSelected: "var(--md-sys-color-primary)",
	/** Selected year text — OnPrimary */
	yearSelectedText: "var(--md-sys-color-on-primary)",
	/** Scrim overlay */
	scrim: "var(--md-sys-color-scrim)",
} as const;

// ─── Shape Tokens ─────────────────────────────────────────────────────────────

/** Shape tokens — corner radii from MD3 shape scale */
export const DP_SHAPE = {
	/** Dialog container — CornerExtraLarge = 28px */
	container: "var(--md-sys-shape-corner-extra-large)",
	/** Day cell — CornerFull = pill */
	day: "var(--md-sys-shape-corner-full)",
	/** Range start/end indicator — CornerFull */
	rangeEndpoint: "var(--md-sys-shape-corner-full)",
	/** Year chip — CornerFull */
	year: "var(--md-sys-shape-corner-full)",
} as const;

// ─── Dimension Tokens ─────────────────────────────────────────────────────────

/** Fixed dimension tokens from DatePickerModalTokens.kt */
export const DP_SIZE = {
	/** Dialog container width */
	containerWidth: 360,
	/** Dialog container height */
	containerHeight: 568,
	/** Day cell width/height */
	dayCell: 40,
	/** Today border width */
	todayOutlineWidth: 1,
	/** Header container height */
	headerHeight: 120,
	/** Range picker header height */
	rangeHeaderHeight: 128,
	/** Year chip height */
	yearChipHeight: 36,
	/** Year chip width */
	yearChipWidth: 72,
} as const;

// ─── Tailwind Class Helpers ───────────────────────────────────────────────────

/**
 * Tailwind utility class strings for Date Picker elements.
 * Used with `cn()` function.
 */
export const DP_CLASSES = {
	/** Dialog container */
	dialog: [
		"bg-m3-surface-container-high",
		"rounded-m3-xl",
		"w-[360px]",
		"overflow-hidden",
		"elevation-3",
	].join(" "),

	/** Day cell container */
	dayCell: [
		"relative flex items-center justify-center",
		"w-10 h-10 rounded-m3-full",
		"text-[length:var(--md-typescale-body-large-size,14px)]",
		"font-[var(--md-typescale-body-large-weight,400)]",
		"cursor-pointer select-none",
		"transition-m3-fast-effects",
	].join(" "),

	/** Day cell: selected state */
	dayCellSelected: ["bg-m3-primary", "text-m3-on-primary"].join(" "),

	/** Day cell: today state */
	dayCellToday: [
		"outline outline-1 outline-m3-primary",
		"text-m3-primary",
	].join(" "),

	/** Day cell: disabled state */
	dayCellDisabled: "opacity-[0.38] pointer-events-none cursor-not-allowed",

	/** Range highlight (between start and end) */
	rangeHighlight: "bg-m3-secondary-container",

	/** Weekday header row */
	weekdayRow:
		"text-[length:var(--md-typescale-body-large-size,14px)] text-m3-on-surface",
} as const;
